import { FileSpreadsheet } from "lucide-react";
import { ChangeEvent, DragEvent, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { SimpleSelect } from "@buildpy/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@buildpy/ui/dialog";
import { formatNumber, formatWon } from "@buildpy/core/lib/format";
import { cn } from "@/lib/utils";
import {
  dataStartOf,
  detectHeader,
  excelRowOf,
  guessSourceVatMode,
  headerAt,
  headerIndexOfInput,
  inferAmountColumn,
  pickDefaultSheet,
  widthOf,
} from "@buildpy/core/modules/quote/import/detect";
import { applyMapping, loadMapping, saveMapping } from "@buildpy/core/modules/quote/import/memory";
import { convertImportPrices, parseImportRows } from "@buildpy/core/modules/quote/import/rows";
import { pendingNoteOf, skippedNoteOf, summarizeImport } from "@buildpy/core/modules/quote/import/summary";
import {
  buildImportTrades,
  matchTrade,
  SKIP_TRADE,
  TradeOption,
} from "@buildpy/core/modules/quote/import/trade-match";
import {
  ColumnMap,
  IMPORT_FIELD_LABEL,
  IMPORT_FIELDS,
  ImportField,
  MAX_IMPORT_BYTES,
  MAX_IMPORT_ROWS,
  RowKind,
} from "@buildpy/core/modules/quote/import/types";
import { ImportFileError, LoadedWorkbook, readWorkbook } from "@buildpy/core/modules/quote/import/xlsx-adapter";
import { loadXlsx } from "@buildpy/core/modules/quote/import/xlsx-load";
import Money from "@/components/money";
import { EditorTrade, ImportTradeInput } from "@buildpy/core/modules/quote/editor/types";
import { VatMode } from "@buildpy/core/modules/money/vat";
import { vatBasisLabel } from "@buildpy/core/modules/quote/snapshot-vat";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 열 매핑을 회사별로 기억하는 열쇠. */
  orgId: string;
  trades: EditorTrade[];
  tradeOptions: TradeOption[];
  spaces: { code: string; label: string }[];
  areaPyeong: number | null;
  /** 견적의 단가 방식 — 원본 단가 기준과 다르면 바꿔 담는다. */
  vatMode?: VatMode;
  onImport: (trades: ImportTradeInput[], rowCount: number) => void;
  /** 닫힌 뒤 포커스를 돌려줄 자리(시트의 숨은 입력칸 · 카드의 여는 버튼). */
  returnFocus?: () => void;
}

const PREVIEW_ROWS = 50;
const ACCEPT = ".xlsx,.xlsm,.xls,.csv";

const COLUMN_OPTIONS = [
  { value: "", label: "버림" },
  ...IMPORT_FIELDS.map((f) => ({ value: f, label: IMPORT_FIELD_LABEL[f] })),
];

const KIND_LABEL: Record<RowKind, string> = {
  item: "항목",
  trade: "공종",
  total: "집계·건너뜀",
  blank: "내역 없음·건너뜀",
};

const KIND_CLASS: Record<RowKind, string> = {
  item: "text-foreground",
  trade: "font-semibold text-primary",
  total: "text-muted-foreground line-through",
  blank: "text-muted-foreground line-through",
};

const SOURCE_VAT_OPTIONS = (["INCL", "EXCL"] as const).map((v) => ({ value: v, label: vatBasisLabel(v) }));

const ImportDialog = ({
  open,
  onOpenChange,
  orgId,
  trades,
  tradeOptions,
  spaces,
  areaPyeong,
  vatMode = "INCL",
  onImport,
  returnFocus,
}: Props) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState("");
  const [book, setBook] = useState<LoadedWorkbook | null>(null);
  const [sheet, setSheet] = useState("");
  // 머리글 줄(격자 index, -1 = 없음). null 이면 추정값.
  const [headerRow, setHeaderRow] = useState<number | null>(null);
  // 「머리글 줄」 칸에 치는 중인 글자 — 한 글자마다 되튀지 않게 blur·Enter 에 확정한다(null = 치는 중 아님).
  const [headerDraft, setHeaderDraft] = useState<string | null>(null);
  const [columnEdits, setColumnEdits] = useState<ColumnMap | null>(null);
  const [assignEdits, setAssignEdits] = useState<Record<string, string>>({});
  // 「원본 단가」 를 고친 값(null = 추정값).
  const [sourceVatEdit, setSourceVatEdit] = useState<VatMode | null>(null);

  // 다이얼로그가 열리면 SheetJS 를 미리 받아 둔다 — 파일을 고를 때 기다림을 줄인다.
  useEffect(() => {
    if (open) void loadXlsx().catch(() => undefined);
  }, [open]);

  const reset = () => {
    setLoading(false);
    setError(null);
    setDragging(false);
    setFileName("");
    setBook(null);
    setSheet("");
    setHeaderRow(null);
    setHeaderDraft(null);
    setColumnEdits(null);
    setAssignEdits({});
    setSourceVatEdit(null);
  };

  const close = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const memory = useMemo(() => (open ? loadMapping(orgId) : {}), [open, orgId]);

  // 짝지을 공종 — 회사 공종 + 견적에 이미 있는 공종(목록에서 빠진 옛 코드 포함).
  const options = useMemo(() => {
    const byCode = new Map<string, TradeOption>(tradeOptions.map((o) => [o.code, o]));
    for (const t of trades) {
      if (!byCode.has(t.trade)) {
        byCode.set(t.trade, { code: t.trade, name: t.name, group: t.group, sort_order: t.sort_order });
      }
    }
    return [...byCode.values()].sort((a, b) => a.sort_order - b.sort_order);
  }, [tradeOptions, trades]);
  const inQuote = useMemo(() => new Set(trades.map((t) => t.trade)), [trades]);
  const assignOptions = useMemo(
    () => [
      { value: "", label: "공종을 고르세요" },
      ...options.map((o) => ({ value: o.code, label: `${o.name}${inQuote.has(o.code) ? " (뒤에 붙임)" : ""}` })),
      { value: SKIP_TRADE, label: "담지 않음" },
    ],
    [options, inQuote]
  );

  const grid = useMemo(() => (book && sheet ? (book.grids[sheet] ?? []) : []), [book, sheet]);
  const width = useMemo(() => widthOf(grid), [grid]);
  const header = useMemo(
    () => (headerRow === null ? detectHeader(grid) : headerAt(grid, headerRow)),
    [grid, headerRow]
  );
  const dataStart = dataStartOf(header);
  const baseColumns = useMemo(
    () => applyMapping(header.labels, header.columns, memory),
    [header, memory]
  );
  // 단가 칸이 비고 「금액」 만 채운 양식은 그 금액을 자재 금액으로 담는다 — 추정이라 기억(saveMapping)에는 남기지 않는다.
  const inferredColumns = useMemo(
    () => inferAmountColumn(grid, baseColumns, dataStart),
    [grid, baseColumns, dataStart]
  );
  const columns = columnEdits ?? inferredColumns;
  const rowNumbers = useMemo(() => (book && sheet ? book.rowNumbers[sheet] : undefined), [book, sheet]);
  const parsed = useMemo(
    () =>
      book
        ? parseImportRows(grid, columns, dataStart, {
            qtyExpr: false,
            area_pyeong: areaPyeong,
            spaces,
          })
        : null,
    [book, grid, columns, dataStart, areaPyeong, spaces]
  );

  const assign = useMemo(() => {
    const out: Record<string, string> = {};
    for (const g of parsed?.groups ?? []) {
      out[g.name] = assignEdits[g.name] ?? matchTrade(g.name, options) ?? "";
    }
    return out;
  }, [parsed, assignEdits, options]);

  const unassigned = (parsed?.groups ?? []).filter((g) => assign[g.name] === "").length;
  const picked = useMemo(
    () => (parsed ? buildImportTrades(parsed.groups, assign, options) : []),
    [parsed, assign, options]
  );
  const pickedRows = picked.reduce((n, t) => n + t.items.length, 0);
  const sourceVat = sourceVatEdit ?? (parsed ? guessSourceVatMode(parsed, vatMode) : vatMode);
  const converting = sourceVat !== vatMode;
  // 담을 합계는 담기(convertImportPrices)와 같은 변환 뒤 값 — 원본 대조만 원본 기준으로 남는다.
  const summary = useMemo(
    () => (parsed ? summarizeImport(parsed, assign, { from: sourceVat, to: vatMode }) : null),
    [parsed, assign, sourceVat, vatMode]
  );

  const loadFile = async (file: File) => {
    setError(null);
    if (file.size > MAX_IMPORT_BYTES) {
      setError("파일이 너무 큽니다 — 10MB 이하로 올려 주세요");
      return;
    }
    if (!/\.(xlsx|xlsm|xls|csv)$/i.test(file.name)) {
      setError("엑셀 파일(.xlsx·.xls)이나 CSV 만 올릴 수 있습니다");
      return;
    }
    setLoading(true);
    try {
      const xlsx = await loadXlsx();
      const bytes = new Uint8Array(await file.arrayBuffer());
      const loaded = readWorkbook(xlsx, bytes, file.name);
      setFileName(file.name);
      setBook(loaded);
      setSheet(pickDefaultSheet(loaded.sheetNames, (n) => loaded.grids[n]?.length ?? 0));
      setHeaderRow(null);
      setHeaderDraft(null);
      setColumnEdits(null);
      setAssignEdits({});
      setSourceVatEdit(null);
    } catch (e) {
      setError(
        e instanceof ImportFileError ? e.message : "파일을 읽지 못했습니다 — 다시 시도해 주세요"
      );
    } finally {
      setLoading(false);
    }
  };

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) void loadFile(file);
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void loadFile(file);
  };

  const changeSheet = (name: string) => {
    setSheet(name);
    setHeaderRow(null);
    setHeaderDraft(null);
    setColumnEdits(null);
    setAssignEdits({});
    setSourceVatEdit(null);
  };

  // 입력은 엑셀에 보이는 행 번호(빈 행 포함, 1부터). 빈 행을 고르면 그 아래 첫 줄로 맞춘다.
  const commitHeaderRow = () => {
    if (headerDraft === null) return;
    const index = headerIndexOfInput(headerDraft, rowNumbers ?? grid.map((_, i) => i + 1));
    setHeaderDraft(null);
    if (index === null || index === header.headerRow) return;
    setHeaderRow(index);
    setColumnEdits(null);
    setAssignEdits({});
  };

  const changeColumn = (index: number, value: string) => {
    const field = value === "" ? null : (value as ImportField);
    const next = [...columns];
    // 같은 곳은 한 열만 — 새로 고른 열이 이긴다(금액 대조 열 제외).
    if (field !== null && field !== "amount_part") {
      next.forEach((f, i) => {
        if (f === field) next[i] = null;
      });
    }
    next[index] = field;
    setColumnEdits(next);
    setAssignEdits({});
  };

  const submit = () => {
    if (!parsed || pickedRows === 0) return;
    if (header.headerRow >= 0) saveMapping(orgId, header.labels, columnEdits ?? baseColumns);
    onImport(convertImportPrices(picked, sourceVat, vatMode), pickedRows);
    close(false);
  };

  const noItemColumn = book !== null && !columns.includes("item");
  // 시트가 줄 상한에서 잘렸으면 넘친 것과 같다 — 뒤쪽 항목이 조용히 빠진다.
  const tooMany = !!parsed && (parsed.tooMany || (book?.truncated.includes(sheet) ?? false));
  const blocked = !parsed || tooMany || noItemColumn || unassigned > 0 || pickedRows === 0;
  const preview = grid.slice(dataStart, dataStart + PREVIEW_ROWS);
  const diff = summary?.diff ?? null;

  return (
    <Dialog open={open} onOpenChange={close}>
      {/* 폰은 칸이 쌓여 높이를 넘으니 창 전체가 스크롤한다 — md 이상은 지금처럼 표만 스크롤. */}
      <DialogContent
        className="flex max-h-[calc(100dvh-2rem)] max-w-5xl flex-col gap-3 overflow-y-auto md:overflow-hidden"
        onCloseAutoFocus={(e) => {
          if (!returnFocus) return;
          e.preventDefault();
          returnFocus();
        }}
        onKeyDown={(e) => {
          // Radix 가 먼저 닫지 못한 경우(안쪽 칸이 키를 먹은 경우)에도 Esc 한 번에 닫는다.
          if (e.key !== "Escape" || e.nativeEvent.isComposing) return;
          e.preventDefault();
          close(false);
        }}
        onOpenAutoFocus={(e) => {
          // 첫 칸 대신 창 자체에 포커스 — 안쪽 칸이 첫 Esc 를 먹지 않게.
          e.preventDefault();
          (e.currentTarget as HTMLElement | null)?.focus({ preventScroll: true });
        }}>
        <DialogHeader className="shrink-0">
          <DialogTitle>엑셀 가져오기</DialogTitle>
          <DialogDescription>
            견적 엑셀의 항목을 공종별로 담습니다. 파일은 이 브라우저에서만 읽고 서버로 보내지 않습니다.
          </DialogDescription>
        </DialogHeader>

        <div
          className={cn(
            "flex shrink-0 flex-wrap items-center gap-3 rounded-lg border border-dashed border-border px-3 py-3 text-sm",
            dragging && "border-primary bg-primary/5"
          )}
          onDragLeave={() => setDragging(false)}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDrop={onDrop}>
          <FileSpreadsheet className="size-5 text-muted-foreground" />
          <span className="text-muted-foreground">
            {loading
              ? "읽는 중…"
              : fileName
                ? fileName
                : "파일을 여기로 끌어 놓거나 고르세요 (.xlsx·.xls·.csv, 10MB·2,000줄까지)"}
          </span>
          <Button
            className="ml-auto"
            disabled={loading}
            size="sm"
            variant="outline"
            onClick={() => fileRef.current?.click()}>
            {book ? "다른 파일" : "파일 고르기"}
          </Button>
          <input ref={fileRef} accept={ACCEPT} className="hidden" type="file" onChange={onPick} />
        </div>

        {error && (
          <p className="shrink-0 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive" role="alert">
            {error}
          </p>
        )}

        {book && parsed && (
          <>
            <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 text-sm">
              {book.sheetNames.length > 1 && (
                <label className="flex items-center gap-2">
                  시트
                  <SimpleSelect
                    aria-label="시트"
                    className="w-44 phone:h-10 phone:min-h-10"
                    options={book.sheetNames.map((n) => ({ value: n, label: n }))}
                    size="xs"
                    value={sheet}
                    onValueChange={changeSheet}
                  />
                </label>
              )}
              <label className="flex items-center gap-2">
                머리글 줄
                <div className="w-16">
                  <Input
                    aria-label="머리글 줄"
                    className="text-right phone:min-h-10"
                    inputMode="numeric"
                    inputSize="xs"
                    name="headerRow"
                    type="text"
                    value={headerDraft ?? excelRowOf(rowNumbers, header.headerRow)}
                    onBlur={() => {
                      // 창·탭이 포커스를 잃어 난 blur 는 확정이 아니다 — 치던 중간값("1")이 적용된다.
                      if (document.hasFocus()) commitHeaderRow();
                    }}
                    onChange={(e) => setHeaderDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key !== "Enter" || e.nativeEvent.isComposing) return;
                      e.preventDefault();
                      commitHeaderRow();
                    }}
                  />
                </div>
                <span className="text-xs text-muted-foreground">
                  {header.headerRow < 0 ? "0 = 머리글 없음" : header.headerRows === 2 ? "두 줄 머리글" : ""}
                </span>
              </label>
              <label className="flex items-center gap-2">
                원본 단가
                <SimpleSelect
                  aria-label="원본 단가"
                  className="w-32 phone:h-10 phone:min-h-10"
                  options={SOURCE_VAT_OPTIONS}
                  size="xs"
                  value={sourceVat}
                  onValueChange={(next) => setSourceVatEdit(next as VatMode)}
                />
              </label>
            </div>

            <div className="min-h-32 flex-1 overflow-auto rounded-md border border-border">
              <table className="w-max min-w-full border-collapse text-xs">
                <thead className="sticky top-0 z-10 bg-muted">
                  <tr>
                    <th className="w-24 border-b border-border px-2 py-1 text-left font-medium">판정</th>
                    {Array.from({ length: width }, (_, c) => (
                      <th key={c} className="min-w-28 border-b border-l border-border px-1 py-1 text-left font-normal">
                        <SimpleSelect
                          aria-label={`${c + 1}번째 열`}
                          arrowKeysChangeValue
                          className="h-7 min-h-7 text-xs phone:h-10 phone:min-h-10"
                          options={COLUMN_OPTIONS}
                          size="xs"
                          value={columns[c] ?? ""}
                          onValueChange={(next) => changeColumn(c, next)}
                        />
                        <span className="mt-0.5 block truncate px-1 text-muted-foreground" title={header.labels[c]}>
                          {header.labels[c] || " "}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.map((row, i) => {
                    const kind = parsed.kinds[dataStart + i];
                    return (
                      <tr key={dataStart + i} className="border-b border-border last:border-0">
                        <td className={cn("whitespace-nowrap px-2 py-1", kind ? KIND_CLASS[kind] : "text-muted-foreground")}>
                          {kind ? KIND_LABEL[kind] : "—"}
                        </td>
                        {Array.from({ length: width }, (_, c) => (
                          <td
                            key={c}
                            className={cn(
                              "max-w-48 truncate border-l border-border px-2 py-1",
                              columns[c] === null && "text-muted-foreground/60",
                              kind && KIND_CLASS[kind]
                            )}
                            title={row[c]}>
                            {row[c] ?? ""}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {grid.length - dataStart > PREVIEW_ROWS && (
              <p className="-mt-1 shrink-0 text-xs text-muted-foreground">
                앞 {PREVIEW_ROWS}줄만 보입니다 · 전체 {grid.length - dataStart}줄을 읽습니다
              </p>
            )}

            {parsed.groups.length > 0 && (
              <div className="flex max-h-[22vh] shrink-0 flex-col gap-1.5 overflow-auto">
                <p className="text-sm font-medium">공종 짝짓기</p>
                <ul className="grid gap-1.5 sm:grid-cols-2">
                  {parsed.groups.map((g) => (
                    <li key={g.name} className="flex items-center gap-2 text-sm">
                      <span className="w-32 truncate" title={g.name}>
                        {g.name || "(공종 칸 없음)"}
                      </span>
                      <span className="w-12 text-xs text-muted-foreground tabular-nums">{g.items.length}줄</span>
                      <span className="text-muted-foreground">→</span>
                      <SimpleSelect
                        aria-invalid={assign[g.name] === ""}
                        aria-label={`${g.name || "공종 없음"} 공종`}
                        arrowKeysChangeValue
                        className="flex-1 phone:h-10 phone:min-h-10"
                        options={assignOptions}
                        size="xs"
                        value={assign[g.name] ?? ""}
                        onValueChange={(next) => setAssignEdits((prev) => ({ ...prev, [g.name]: next }))}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex shrink-0 flex-col gap-1 rounded-md bg-muted/50 px-3 py-2 text-sm">
              <p className="tabular-nums">
                <b>{pickedRows}줄</b> · 공종 {picked.length}개 · 소계·합계 {parsed.skippedTotals}줄 건너뜀
                {parsed.skippedBlank > 0 && ` · 내역 없는 줄 ${parsed.skippedBlank}개 건너뜀`}
                {parsed.unclearCells > 0 && (
                  <span className="text-destructive"> · 못 읽은 칸 {parsed.unclearCells}개(0·빈칸으로 담김)</span>
                )}
              </p>
              {summary && skippedNoteOf(summary) !== null && (
                <p className="tabular-nums text-muted-foreground">{skippedNoteOf(summary)}</p>
              )}
              {summary && summary.pickedTotal !== null && (
                <p className="tabular-nums text-muted-foreground">
                  담을 합계{converting && `(${vatBasisLabel(vatMode)})`} <Money value={summary.pickedTotal} />
                  {pendingNoteOf(summary)}
                  {(summary.skippedRows > 0 || converting) && summary.readTotal !== null && (
                    <>
                      {` · 읽은 합계${converting ? `(${vatBasisLabel(sourceVat)})` : ""} `}
                      <Money value={summary.readTotal} />
                    </>
                  )}
                  {parsed.originalTotal !== null && (
                    <>
                      {` · 원본 ${parsed.originalTotalLabel ?? "합계"} `}
                      <Money value={parsed.originalTotal} />
                    </>
                  )}
                  {diff !== null &&
                    (diff === 0 ? (
                      <span className="text-foreground"> · 일치</span>
                    ) : summary.warn ? (
                      <span className="text-destructive"> · 차이 <Money value={diff} /></span>
                    ) : (
                      <span className="text-muted-foreground"> · 차이 <Money value={diff} />(1% 미만)</span>
                    ))}
                </p>
              )}
              {parsed.indirectTotal > 0 && (
                <p className="tabular-nums text-muted-foreground">
                  담지 않음 — 간접비(공과잡비·이윤 등) <Money value={parsed.indirectTotal} />
                </p>
              )}
              {parsed.pricedFromAmount > 0 && (
                <p className="text-muted-foreground">
                  단가 열이 없어 금액÷수량으로 단가를 채웠습니다 — 확인해 주세요
                </p>
              )}
              {converting && (
                <p className="text-muted-foreground" data-testid="import-vat-convert">
                  원본 단가({vatBasisLabel(sourceVat)})를 견적 방식({vatBasisLabel(vatMode)})에 맞춰{" "}
                  {vatMode === "EXCL" ? "÷1.1" : "×1.1"} 해 담습니다
                  {parsed.vatSplitRows && sourceVatEdit === null && " — 원본에 공급가액·부가세 줄이 있어 별도로 골랐습니다"}
                </p>
              )}
              {parsed.expenseMerged && (
                <p className="text-muted-foreground">경비는 노무 단가에 합쳤습니다</p>
              )}
              {tooMany && (
                <p className="text-destructive">
                  {formatNumber(MAX_IMPORT_ROWS)}줄을 넘습니다 — 시트를 나눠 올려 주세요
                </p>
              )}
              {summary?.warn && (
                <p
                  className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 font-medium text-destructive"
                  role="alert">
                  {summary.readTotal === 0
                    ? "읽은 금액이 0원입니다 — 단가·금액 열 매핑을 확인해 주세요"
                    : `원본 합계와 ${formatWon(Math.round(Math.abs(diff ?? 0)))} 차이 납니다 — 열 매핑을 확인해 주세요`}
                </p>
              )}
              {noItemColumn && <p className="text-destructive">「작업내역」 열을 골라 주세요</p>}
              {unassigned > 0 && <p className="text-destructive">공종을 고르지 않은 덩이가 {unassigned}개 있습니다</p>}
            </div>
          </>
        )}

        <DialogFooter className="shrink-0">
          <Button disabled={blocked} size="sm" variant="primary" onClick={submit}>
            {summary?.warn && pickedRows > 0
              ? "차이가 있어도 담기"
              : pickedRows > 0
                ? `${pickedRows}줄 담기`
                : "담기"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportDialog;
